import React, { useCallback, useEffect, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import {
  fetchCosmeticBadgesCatalog,
  fetchProfileCosmeticBadges,
  purchaseCosmeticBadgeGrooves,
  purchaseSellerStats30d,
  setCosmeticBadgeEquipped,
} from '../../lib/groovesSpendService.js';
import {
  FILTER_SLOT_EXTRA_COST,
  GROOVES_PHONE_GATE_USER_MESSAGE,
  LISTING_FEATURE_COST,
  SELLER_STATS_30D_COST,
  profileGroovesBalance,
} from '../constants/groovesEconomy.js';

/**
 * Tienda de canjes Grooves: insignias cosméticas (+ acceso stats).
 */
export default function GroovesCanjeShop({
  userId,
  profile,
  phoneVerified,
  groovesBalance,
  onBalanceChange,
  onStatsPurchased,
  onCosmeticChange,
  compact = false,
  showStatsPurchase = true,
}) {
  const [catalog, setCatalog] = useState([]);
  const [owned, setOwned] = useState([]);
  const [equippedSlugs, setEquippedSlugs] = useState([]);
  const [loading, setLoading] = useState(!!userId);
  const [busySlug, setBusySlug] = useState(null);
  const [statsBusy, setStatsBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const reload = useCallback(async () => {
    if (!userId) {
      setCatalog([]);
      setOwned([]);
      setEquippedSlugs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [cat, prof] = await Promise.all([
        fetchCosmeticBadgesCatalog(),
        fetchProfileCosmeticBadges(userId),
      ]);
      setCatalog(cat);
      setOwned(prof.owned ?? []);
      setEquippedSlugs((prof.equipped ?? []).map(b => b.slug));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handlePurchaseBadge = async slug => {
    if (!phoneVerified) {
      setErr(GROOVES_PHONE_GATE_USER_MESSAGE);
      return;
    }
    setErr('');
    setMsg('');
    setBusySlug(slug);
    const res = await purchaseCosmeticBadgeGrooves(slug);
    setBusySlug(null);
    if (!res.ok) {
      setErr(res.message || 'No se pudo comprar la insignia.');
      return;
    }
    setMsg(`Insignia «${res.label || slug}» adquirida.`);
    if (res.grooves_balance != null) onBalanceChange?.(res.grooves_balance);
    await reload();
    onCosmeticChange?.();
  };

  const handleToggleEquip = async (slug, currentlyEquipped) => {
    setErr('');
    setMsg('');
    setBusySlug(slug);
    const res = await setCosmeticBadgeEquipped(slug, !currentlyEquipped);
    setBusySlug(null);
    if (!res.ok) {
      setErr(res.message || 'No se pudo equipar.');
      return;
    }
    setEquippedSlugs(res.equipped ?? []);
    await reload();
    onCosmeticChange?.();
  };

  const handlePurchaseStats = async () => {
    if (!phoneVerified) {
      setErr(GROOVES_PHONE_GATE_USER_MESSAGE);
      return;
    }
    setErr('');
    setMsg('');
    setStatsBusy(true);
    const res = await purchaseSellerStats30d();
    setStatsBusy(false);
    if (!res.ok) {
      setErr(res.message || 'No se pudo activar estadísticas.');
      return;
    }
    const until = res.seller_stats_until ? new Date(res.seller_stats_until).toLocaleDateString('es-ES') : '';
    setMsg(`Estadísticas premium activas hasta ${until}.`);
    if (res.grooves_balance != null) onBalanceChange?.(res.grooves_balance);
    onStatsPurchased?.();
  };

  const statsActive =
    profile?.seller_stats_until && new Date(profile.seller_stats_until).getTime() > Date.now();

  if (!userId) return null;

  return (
    <div style={{ marginBottom: compact ? 0 : 12 }}>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 8 }}>Canjear Grooves</div>
      <p style={{ margin: '0 0 10px', fontSize: 10, color: 'rgba(255,255,255,0.38)', lineHeight: 1.45 }}>
        Insignias de perfil (no verificación comercial). Referencia: destacar anuncio {LISTING_FEATURE_COST} G · filtro extra{' '}
        {FILTER_SLOT_EXTRA_COST} G.
      </p>
      {err ? <div style={{ fontSize: 11, color: C.danger, marginBottom: 8, lineHeight: 1.4 }}>{err}</div> : null}
      {msg ? (
        <div style={{ fontSize: 11, color: 'rgba(200,168,75,0.95)', marginBottom: 8, lineHeight: 1.4 }}>{msg}</div>
      ) : null}

      {showStatsPurchase && !compact ? (
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 10,
            marginBottom: 10,
            background: 'rgba(255,255,255,0.04)',
            border: '0.5px solid rgba(200,168,75,0.22)',
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>Estadísticas de vendedor · 30 días</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 4, lineHeight: 1.45 }}>
            Visitas 30 d, ingresos, favoritos, top anuncios e insights. Visible en Mis ventas.
          </div>
          {statsActive ? (
            <div style={{ fontSize: 10, color: C.green, marginTop: 6 }}>
              Activo hasta {new Date(profile.seller_stats_until).toLocaleDateString('es-ES')}
            </div>
          ) : null}
          <button
            type="button"
            disabled={statsBusy || !phoneVerified}
            onClick={handlePurchaseStats}
            style={{
              marginTop: 8,
              padding: '7px 12px',
              borderRadius: 8,
              border: `0.5px solid ${C.gold}`,
              background: statsBusy ? 'rgba(200,168,75,0.08)' : 'rgba(200,168,75,0.15)',
              color: C.gold,
              fontSize: 11,
              fontWeight: 700,
              cursor: statsBusy || !phoneVerified ? 'not-allowed' : 'pointer',
              opacity: !phoneVerified ? 0.55 : 1,
            }}
          >
            {statsBusy ? '…' : statsActive ? `Renovar · ${SELLER_STATS_30D_COST} G` : `Activar · ${SELLER_STATS_30D_COST} G`}
          </button>
        </div>
      ) : null}

      {loading ? (
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Cargando tienda…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {catalog.map(row => {
            const isOwned = owned.some(o => o.slug === row.slug);
            const isEquipped = equippedSlugs.includes(row.slug);
            const cost = row.cost ?? 0;
            const canBuy = phoneVerified && !isOwned && profileGroovesBalance({ grooves: groovesBalance }) >= cost;
            const busy = busySlug === row.slug;
            return (
              <div
                key={row.slug}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 10,
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: 'rgba(255,255,255,0.04)',
                  border: `0.5px solid ${isEquipped ? 'rgba(200,168,75,0.4)' : 'rgba(255,255,255,0.08)'}`,
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: isEquipped ? C.gold : '#fff' }}>{row.label}</div>
                  {row.description ? (
                    <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.42)', marginTop: 4, lineHeight: 1.4 }}>
                      {row.description}
                    </div>
                  ) : null}
                  {!isOwned ? <div style={{ fontSize: 11, color: C.gold, marginTop: 6, fontWeight: 600 }}>{cost} G</div> : null}
                </div>
                {isOwned ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleToggleEquip(row.slug, isEquipped)}
                    style={{
                      flexShrink: 0,
                      padding: '7px 12px',
                      borderRadius: 8,
                      border: `0.5px solid ${isEquipped ? C.green : C.gold}`,
                      background: isEquipped ? 'rgba(93,202,165,0.12)' : 'rgba(200,168,75,0.12)',
                      color: isEquipped ? C.green : C.gold,
                      fontSize: 10,
                      fontWeight: 700,
                      cursor: busy ? 'wait' : 'pointer',
                    }}
                  >
                    {busy ? '…' : isEquipped ? 'Equipada' : 'Equipar'}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={busy || !canBuy}
                    onClick={() => handlePurchaseBadge(row.slug)}
                    style={{
                      flexShrink: 0,
                      padding: '7px 12px',
                      borderRadius: 8,
                      border: `0.5px solid ${C.gold}`,
                      background: canBuy ? 'rgba(200,168,75,0.18)' : 'rgba(255,255,255,0.04)',
                      color: canBuy ? C.gold : 'rgba(255,255,255,0.35)',
                      fontSize: 10,
                      fontWeight: 700,
                      cursor: busy || !canBuy ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {busy ? '…' : 'Comprar'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
